import React, { useState, useEffect, useContext, useMemo } from 'react';
import { Stack, IconButton, Tooltip, Pagination } from '@mui/material';
import RowContainer from './components/RowContainer';
import NotificationMessage from './components/NotificationMessage';
import { BsDownload } from 'react-icons/bs';
import { AiOutlineDelete } from 'react-icons/ai';
import FileIcon from 'assets/img/FileIcon.svg';
import AddDocumentModal from './components/AddDocumentModal';
import { toast } from 'react-toastify';
//redux
import { connect, useSelector } from 'react-redux';
import { DeleteMerchantExtraDocument } from 'store/actions/merchantActions';
import LoadingState from 'components/new_components/LoadingState';
import EmptyState from 'components/new_components/EmptyState';
import AppDeleteModal from 'components/new_components/AppDeleteModal';
import { formatDateObjectHandler } from 'utils';
import { SelectedMerchantContext } from 'store/context/SelectedMerchantContextProvider';
import { getType } from 'utils';
import { GetDownloadLink } from 'store/actions/generalActions';
import { GetAllMerchantsExtraDocs } from 'api/dataroom';
import { useQuery, useQueryClient } from '@tanstack/react-query';

const PAGE_SIZE = 25;
const NewDoc = (props) => {
    const [addModalOpen, setAddModalOpen] = useState(false);
    const [deleteModalOpen, setDeleteModalOpen] = useState(false);
    const [deletePayload, setDeletePayload] = useState(null);
    const [deleting, setDeleting] = useState(false);

    const { currentTab, DeleteMerchantExtraDocument } = props;
    const user_type = useSelector((state) => state?.authReducers?.user_type);
    const client_id = useSelector((state) => state?.implementerReducer?.client_id);

    const [page, setPage] = useState(1);
    const [count, setCount] = useState(0);

    //context api for auditor's selected merchant
    const { activeMerchant } = useContext(SelectedMerchantContext);
    // Invalidate query
    const queryClient = useQueryClient();
    const totalPages = Math.ceil(count / PAGE_SIZE);

    const query_params = useMemo(() => {
        const params = {
            ...(page && { page: typeof page === 'number' ? page : page?.current }),
            ...(page && { page_size: PAGE_SIZE }),
            ...((user_type === 'implementer' || user_type === 'auditor') && {
                merchant: user_type === 'implementer' ? client_id : activeMerchant,
            }),
        };
        return params;
    }, [page, client_id, activeMerchant]);

    // Async functions
    const { data: docs, isLoading } = useQuery({
        queryKey: ['clientUploads', query_params],
        queryFn: () => GetAllMerchantsExtraDocs(query_params),
        retry: 1,
    });

    const openAddModal = () => {
        setAddModalOpen(true);
    };
    const closeAddModal = () => {
        setAddModalOpen(false);
    };
    const openDeleteModal = (id) => {
        setDeletePayload(id);
        setDeleteModalOpen(true);
    };
    const closeDeleteModal = () => {
        setDeleteModalOpen(false);
        setDeletePayload(null);
    };

    const handleDeleteDocument = async () => {
        setDeleting(true);
        const res = await DeleteMerchantExtraDocument(deletePayload);
        setDeleting(false);
        if (res?.success) {
            setPage(1);
            queryClient.invalidateQueries('clientUploads');
            toast.success('Document Deleted Successfully');
            closeDeleteModal();
        } else {
            toast.error(res?.message);
        }
    };

    useEffect(() => {
        if (docs) {
            setCount(docs?.count);
        }
    }, [docs]);

    const handleChange = (_, value) => {
        setPage(value);
    };
    return (
        <>
            <Stack sx={{ width: '80%', margin: '0 auto', my: 2 }}>
                <NotificationMessage currentTab={currentTab} openAddModal={openAddModal} />
                {isLoading ? (
                    <LoadingState />
                ) : count > 0 ? (
                    <>
                        {docs?.results
                            ?.sort((a, b) => b?.id - a?.id)
                            ?.map((data) => (
                                <DataItem data={data} key={data.id} openDeleteModal={openDeleteModal} />
                            ))}
                        {count > 25 && (
                            <div className="flex justify-end items-center">
                                <Pagination
                                    shape="rounded"
                                    count={totalPages}
                                    page={page}
                                    onChange={handleChange}
                                    color="primary"
                                />
                            </div>
                        )}
                    </>
                ) : (
                    <EmptyState description="No Document(s) Available" />
                )}
            </Stack>
            <AddDocumentModal open={addModalOpen} handleClose={closeAddModal} />
            <AppDeleteModal
                open={deleteModalOpen}
                handleClose={closeDeleteModal}
                title="Permanently delete this Document?"
                subtitle="Please note that this action can not be reversed."
                deleting={deleting}
                onDelete={handleDeleteDocument}
            />
        </>
    );
};

const DataItem = ({ data, openDeleteModal }) => {
    const role = getType('role');

    const [loadingLink, setLoadingLink] = useState(false);
    const getDownloadLink = async () => {
        if (data?.doc_file) {
            setLoadingLink(true);
            const res = await GetDownloadLink('compliance', 'doc_file_upload', data.id)();
            setLoadingLink(false);
            if (res?.success) {
                // open decoded url
                window.open(res?.data, '_blank');
            } else {
                toast.error('Something went wrong!');
            }
        }
    };

    return (
        <RowContainer key={data?.id}>
            <Stack flexDirection="row" alignItems="center" gap={1}>
                <img src={FileIcon} alt="file icon" className="w-[26px]" />
                <div className="flex flex-row items-center">
                    <p className="text-[13px] font-medium text-[#3E414D] m-0">{data?.name}</p>
                    <span className="text-[11px] font-semibold  pl-1">
                        ({formatDateObjectHandler(data?.date_created, 'MMMM Do, YYYY') || 'Nil'})
                    </span>
                </div>
            </Stack>
            <Stack flexDirection="row" alignItems="center">
                <Tooltip title="Download Document" placement="top">
                    <IconButton aria-label="download" onClick={getDownloadLink}>
                        {loadingLink ? (
                            <LoadingState size={20} styles={{ justifyContent: 'start', alignItems: 'start' }} />
                        ) : (
                            <BsDownload color="#77777A" size="18px" />
                        )}
                    </IconButton>
                </Tooltip>
                {role !== 'auditor' && (
                    <Tooltip title="Delete Document" placement="top">
                        <IconButton aria-label="delete" onClick={() => openDeleteModal(data?.id)}>
                            <AiOutlineDelete color="#77777A" size="18px" />
                        </IconButton>
                    </Tooltip>
                )}
            </Stack>
        </RowContainer>
    );
};

const mapStateToProps = () => {
    return {};
};
export default connect(mapStateToProps, { DeleteMerchantExtraDocument })(NewDoc);
